import React, { useMemo, Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';

import {
  SortingState,
  GroupingState,
  IntegratedGrouping,
  FilteringState,
  RowDetailState,
} from '@devexpress/dx-react-grid';

import {
  Grid,
  Table,
  TableHeaderRow,
  TableGroupRow,
  TableFilterRow,
  TableRowDetail,
  Toolbar,
  GroupingPanel,
  DragDropProvider,
} from '@devexpress/dx-react-grid-bootstrap3';

import StringFilterHeaderCell from './StringFilterHeaderCell';
import { DateFilterHeaderCell } from './DateFilterHeaderCell';
import TagFilterHeaderCell from './TagFilterHeaderCell';
import TagIndicatorFilterHeaderCell from './TagIndicatorFilterHeaderCell';
import ButtonsCell from './ButtonsCell';
import { TagCell } from './TagCell';
import TagIndicatorCell from './TagIndicatorCell';
import { MultiLineTextCell } from './MultiLineTextCell';
import DataGridHeader from './DataGridHeader';
import DataGridFooter from './DataGridFooter';
import { Loading } from './Loading';
import { appConfig } from 'config/appConfig';

import style from './style.scss';
import SelectFilterHeaderCell from './SelectFilterHeaderCell';

const initialDefaultSorting = {
  columnName: null,
  direction: 'asc',
};

export const LargeDataTable = (props) => {
  const { columns, defaultSorting, rows, rowCount, tags, hasDetailRows, hasGrouping, hasSorting, hasStatusAlias, loading, onChangeExpandedDetails, expandedRowIds, infiniteScroll } = props;
  const [isFetching, setIsFetching] = useState(false);
  const [grouping, setGrouping] = useState([]);
  const [queries, setQueries] = useState({
    pageLength: appConfig.limit,
    pageOffset: 0,
    keys: columns.map(col => ({
      type: col.type,
      columnName: col.name,
      data: '',
      conditionIndex: (col.type === 'text' || col.type === 'multi-text' || col.type === 'tag' || col.type === 'tag-text') ? 1 : 0,
    })),
    sorting: defaultSorting ? defaultSorting[0] : initialDefaultSorting,
  });
  const [filterValues, setFilterValues] = useState({});
  const tableColumnExtensions = useMemo(
    () => columns
      .filter(col => col.width !== undefined && col.width !== null)
      .map(col => ({ columnName: col.name, width: col.width, align: col.align }))
    , [columns]
  );

  useEffect(() => {
    const filterValuesTemp = {};
    props.columns.forEach((col) => {
      filterValuesTemp[col.name] = '';
    });
    setFilterValues(filterValuesTemp);
  }, []);

  const loadData = (newQueries, isInitial = false, isHeaderTag = false) => {
    const { onFilter } = props;
    onFilter(newQueries, isInitial, isHeaderTag);
  };

  useEffect(() => {
    loadData(queries, true);
  }, []);

  useEffect(() => {
    const queriesTemp = _.extend({}, queries, { pageOffset: props.pageOffset.offset });
    setQueries(queriesTemp);
    setIsFetching(false);
  }, [props.pageOffset]);

  const getColumnIndex = (colurmName) => {
    let index = -1;
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].name === colurmName) {
        index = i;
        break;
      }
    }
    return index;
  };

  const setFilter = (columnName, filterData) => {
    const queriesTemp = _.extend({}, queries, { pageOffset: 0 });
    const filterValuesTemp = { ...filterValues };
    const index = getColumnIndex(columnName);
    queriesTemp.keys[index].data = filterData.data;
    queriesTemp.keys[index].conditionIndex = filterData.conditionIndex;
    filterValuesTemp[columnName] = filterData.data;
    setQueries(queriesTemp);
    setFilterValues(filterValuesTemp);
    loadData(queriesTemp);
  };

  const handlePageLengthChange = (event) => {
    const queriesTemp = _.extend({}, queries);
    queriesTemp.pageLength = event.target.value;
    setQueries(queriesTemp);
    loadData(queriesTemp);
  };

  const handlePageOffsetChange = (selectData) => {
    const queriesTemp = _.extend({}, queries);
    queriesTemp.pageOffset = selectData.selected * queriesTemp.pageLength;
    setQueries(queriesTemp);
    loadData(queriesTemp);
  };

  const changeSorting = (sorting) => {
    const queriesTemp = _.extend({}, queries);
    const sortingObj = {
      columnName: sorting[0].columnName === 'statusAlias' ? 'StatusAliases' : sorting[0].columnName,
      direction: sorting[0].direction,
    };

    // Use correct fields to improve speed
    switch (sortingObj.columnName) {
      case 'purpose':
        sortingObj.columnName = 'purpose.Label';
        break;
      case 'applicationDate':
        sortingObj.columnName = 'applicationDate.DateTime';
        break;
      case 'initiatedDate':
        sortingObj.columnName = 'initiatedDate.DateTime';
        break;
      default:
    }

    queriesTemp.sorting = sortingObj;
    setQueries(queriesTemp);
    loadData(queriesTemp);
  };

  const handleHeaderTagSelect = (activeTags) => {
    const queriesTemp = _.extend({}, queries);
    const { keys } = queriesTemp;
    for (let i = 0; i < keys.length; i++) {
      if (keys[i].type === 'htag') {
        keys[i].data = activeTags;
      }
    }
    queriesTemp.activeTags = activeTags;
    setQueries(queriesTemp);
    loadData(queriesTemp, false, true);
  };

  const rowDetailRender = (rowDetailProps) => {
    const { row } = rowDetailProps;
    return (
      <div>
        {props.hasDetailRender && (
          props.detailRender(row)
        )}
        {!props.hasDetailRender &&
          <Grid
            rows={row.detailRows}
            columns={props.detailColumns}
          >
            <Table
              columnExtensions={props.detailColumnExtensions}
            />
            <TableHeaderRow />
          </Grid>
        }
      </div>
    );
  };

  const changeGrouping = (newGrouping) => {
    setGrouping(newGrouping);
  };

  const handleRowClick = (applicationId, event) => {
    if (!props.isPreSelection) {
      const target = (event.ctrlKey || event.metaKey) ? '_blank' : '_self';
      window.open(`/dashboard/application-detail/?&applicationId=${applicationId}`, target);
    }
  };

  const cellDetailRender = (cellDetailProps) => {
    const { row, ...restProps } = cellDetailProps;
    if (row.isExpand) {
      return <TableRowDetail.ToggleCell row={row} {...restProps} />;
    }
    return (
      <TableRowDetail.ToggleCell
        style={{
          pointerEvents: 'none',
          opacity: 0.5,
        }}
      />
    );
  };

  const tableContainerComponent = (children, showToolbar) => (
    <div className="table-responsive" style={{ marginTop: `${showToolbar ? 55 : 0}px` }}>
      { children }
    </div>
  );

  const tableHeadComponent = (children, showToolbar) => (
    <thead style={{ top: `${showToolbar ? 55 : 0}px` }}>
      {children}
    </thead>
  );

  const handleLoadMore = () => {
    const total = rowCount || rows.length;
    const queriesTemp = _.extend({}, queries);
    if (queriesTemp.pageOffset < total) {
      queriesTemp.pageLength = 50;
      queriesTemp.pageOffset = queriesTemp.pageOffset > 0
        ? queriesTemp.pageOffset + 50
        : 100;
      setQueries(queriesTemp);
      loadData(queriesTemp);
    }
  };

  const handleScroll = (e) => {
    const total = rowCount || rows.length;
    if (!isFetching && total > 100 && infiniteScroll && rowCount > 0 && queries.pageOffset + 50 < total) {
      const bottom = (e.target.scrollHeight - e.target.scrollTop) < e.target.clientHeight + 1;
      if (bottom) {
        setIsFetching(true);
        handleLoadMore();
      }
    }
  };

  return (
    <Fragment>
      <div className={classNames(style['virtual-data-table'], props.className)} onScroll={handleScroll}>
        <DataGridHeader
          tags={tags}
          pageLength={queries.pageLength}
          onPageLengthChange={handlePageLengthChange}
          onTagSelected={handleHeaderTagSelect}
        />
        <Grid
          rows={rows}
          columns={columns}
        >
          {hasGrouping && <DragDropProvider />}
          <SortingState onSortingChange={changeSorting} defaultSorting={defaultSorting} />
          {
            (hasGrouping) ?
              <GroupingState
                grouping={grouping}
                onGroupingChange={changeGrouping}
              /> :
              null
          }
          {
            hasDetailRows && expandedRowIds.length ?
              <RowDetailState
                expandedRowIds={expandedRowIds}
                defaultExpandedRowIds={[]}
                onExpandedRowIdsChange={onChangeExpandedDetails}
              />
            :
              <RowDetailState
                defaultExpandedRowIds={[]}
                onExpandedRowIdsChange={onChangeExpandedDetails}
              />
          }
          { (hasGrouping) ? <IntegratedGrouping /> : null }
          <Table
            cellComponent={(cellComponentProps) => {
              const { row, column } = cellComponentProps;

              switch (column.type) {
                case 'tag':
                  return <TagCell tags={row.tags} type={column.type} onClick={() => column.moreOptions.buttonClick()} />;
                case 'tag-text':
                  return <TagCell tags={row.activity} type={column.type} onClick={() => column.moreOptions.buttonClick()} />;
                case 'tag-indicator':
                  return <TagIndicatorCell name={column.name} title={column.title} style={column.moreOptions.style} info={row} />;
                case 'buttons':
                  return (
                    <ButtonsCell
                      buttons={column.moreOptions.buttons}
                      info={row}
                      onClick={index => column.moreOptions.buttonClick(index, row)}
                    />
                  );
                case 'multi-text':
                  return <MultiLineTextCell text={row.description} />;
                case 'link':
                  return (
                    <td
                      onClick={handleRowClick.bind(null, row.applicationId)}
                      style={{ padding: 0 }}
                    >
                      <div className="clickable">
                        {row.applicationId}
                      </div>
                    </td>
                  );
                case 'status':
                  return <td><span className={classNames(style[`status-${row.statusCode}`], 'badge', style.status)}>{hasStatusAlias ? row.statusAlias : row.statusLabel}</span></td>;
                case 'settlementStatus':
                  return (
                    <td>
                      <span className={classNames(style[`status-${row.settlementStatus || 'Empty'}`], 'badge', style.status)}>
                        { row.settlementStatus || '-' }
                      </span>
                    </td>
                  );
                default:
                  return <Table.Cell {...cellComponentProps} />;
              }
            }}
            noDataCellComponent={({ getMessage, ...restProps }) => (
              <Table.NoDataCell
                {...restProps}
                getMessage={getMessage}
                style={{ padding: '150px 0' }}
              />
            )}
            columnExtensions={tableColumnExtensions}
            containerComponent={({ children }) => tableContainerComponent(children, hasGrouping)}
            headComponent={({ children }) => tableHeadComponent(children, hasGrouping)}
          />
          {/* <TableColumnReordering
            defaultOrder={['id', 'applicant', 'date', 'ssn', 'details']}
          /> */}
          <TableHeaderRow
            showSortingControls={hasSorting}
            allowDragging={hasGrouping}
            cellComponent={(cellComponentProps) => {
              const { column } = cellComponentProps;
              if (column.moreOptions && column.moreOptions.isEmptyHeader) {
                return <td />;
              }
              return (
                <TableHeaderRow.Cell
                  {...cellComponentProps}
                  className={column.headerClass ? column.headerClass : ''}
                />
              );
            }}
          />
          { (hasGrouping) ? <TableGroupRow /> : null }
          { (hasGrouping) ? <Toolbar rootComponent={({ children }) => {
            const header = document.querySelector('.card-header');
            return (
              <div className="panel-heading" style={{ top: header ? '51px' : '0' }}>
                { (rows && rows.length && infiniteScroll)
                  ? <span className={style.counter} style={{ top: header ? '-36px' : '-30px' }}>Showing {rows.length} of {rowCount}</span>
                  : null
                }
                {children}
              </div>
            );
          }}
          /> : null }
          { (hasGrouping) ? <GroupingPanel allowDragging allowSorting /> : null }
          <FilteringState
            onFiltersChange={() => {}}
          />
          <TableFilterRow
            cellComponent={({ column }) => {
              let conditionIndex = 0;
              queries.keys.forEach((item) => {
                if (item.columnName === column.name) {
                  conditionIndex = item.conditionIndex;
                }
              });

              if (
                column.type === 'text' ||
                column.type === 'multi-text' ||
                column.type === 'link' ||
                column.type === 'status' ||
                column.type === 'settlementStatus'
              ) {
                return <StringFilterHeaderCell setFilter={filterData => setFilter(column.name, filterData)} value={filterValues[column.name]} conditionIndex={conditionIndex} type={column.valueType} />;
              } else if (column.type === 'date') {
                return <DateFilterHeaderCell type={column.type} setFilter={filterData => setFilter(column.name, filterData)} value={filterValues[column.name]} conditionIndex={conditionIndex} />;
              } else if (column.type === 'tag' || column.type === 'tag-text') {
                return <TagFilterHeaderCell setFilter={filterData => setFilter(column.name, filterData)} value={filterValues[column.name]} conditionIndex={conditionIndex} />;
              } if (column.type === 'select') {
                return <SelectFilterHeaderCell value={filterValues[column.name]} options={column.options || []} setFilter={filterData => setFilter(column.name, filterData)} />;
              } else if (column.type === 'tag-indicator') {
                return <TagIndicatorFilterHeaderCell />;
              }
              return <th />;
            }}
          />
          {
            hasDetailRows &&
            <TableRowDetail
              contentComponent={rowDetailRender}
              toggleCellComponent={cellDetailRender}
            />
          }
        </Grid>
      </div>
      {!infiniteScroll && (
        <DataGridFooter
          pageLength={queries.pageLength}
          pageOffset={queries.pageOffset}
          rowCount={rowCount}
          onPageLengthChange={handlePageLengthChange}
          onPageOffsetChange={handlePageOffsetChange}
        />
      )}
      {(loading || isFetching) && <Loading />}
    </Fragment>
  );
};

LargeDataTable.propTypes = {
  className: PropTypes.string,
  columns: PropTypes.array.isRequired,
  defaultSorting: PropTypes.array,
  detailColumnExtensions: PropTypes.array,
  detailColumns: PropTypes.array,
  rowCount: PropTypes.number.isRequired,
  rows: PropTypes.array.isRequired,
  tags: PropTypes.array,
  hasDetailRows: PropTypes.bool,
  hasGrouping: PropTypes.bool,
  hasSorting: PropTypes.bool,
  hasStatusAlias: PropTypes.bool,
  isLoading: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  loading: PropTypes.bool,
  menuFilter: PropTypes.string,
  onFilter: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  pageOffset: PropTypes.object,
  isPreSelection: PropTypes.bool,
  hasDetailRender: PropTypes.bool,
  detailRender: PropTypes.func,
  onChangeExpandedDetails: PropTypes.func,
  expandedRowIds: PropTypes.array,
  infiniteScroll: PropTypes.bool,
};

LargeDataTable.defaultProps = {
  className: '',
  defaultSorting: [],
  detailColumns: null,
  detailColumnExtensions: null,
  tags: [],
  hasDetailRows: false,
  hasGrouping: false,
  hasSorting: false,
  hasStatusAlias: false,
  isLoading: false,
  loading: false,
  menuFilter: '',
  pageOffset: null,
  isPreSelection: false,
  hasDetailRender: false,
  detailRender: () => {},
  onChangeExpandedDetails: () => {},
  expandedRowIds: [],
  infiniteScroll: false,
};

export default withRouter(LargeDataTable);



// WEBPACK FOOTER //
// ./src/components/Tables/LargeDataTable.js